import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";
import { Heart, Plus, Search as SearchIcon } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useToast } from "@/components/ui/use-toast";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";
import { podcastService } from "@/lib/podcastService";

interface Podcast {
  id: string;
  title: string;
  description: string;
  image_url: string;
  subscriber_count: number;
}

const Search = () => {
  const [podcasts, setPodcasts] = useState<Podcast[]>([]);
  const [loading, setLoading] = useState(true);
  const [subscribedPodcastIds, setSubscribedPodcastIds] = useState<string[]>(
    [],
  );
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [feedUrl, setFeedUrl] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const { toast } = useToast();
  const { user, isAdmin } = useAuth();
  
  // Debug logging for admin status
  useEffect(() => {
    console.log("Search component - Auth state:", { 
      userId: user?.id, 
      isLoggedIn: !!user, 
      isAdmin: isAdmin 
    });
    
    // Additional check to verify admin status
    if (user?.id) {
      // List of admin IDs - keep in sync with AuthContext
      const adminIds = ["8e62a312-e432-4f9e-b399-ca093abd5422"];
      const isUserAdmin = adminIds.includes(user.id);
      console.log(`Direct admin check in Search component: ${isUserAdmin}`);
    }
  }, [user, isAdmin]);
  const navigate = useNavigate();
  const scrollRef = useRef<HTMLDivElement>(null);

  // Filter podcasts based on search query
  const filteredPodcasts = useMemo(() => {
    if (!searchQuery) return podcasts;
    const query = searchQuery.toLowerCase();
    return podcasts.filter(
      (podcast) =>
        podcast.title.toLowerCase().includes(query) ||
        podcast.description.toLowerCase().includes(query),
    );
  }, [podcasts, searchQuery]);

  useEffect(() => {
    loadPodcasts();
    if (user) {
      loadSubscriptions();
    }
  }, [user]);

  const loadPodcasts = async (forceRefresh = false) => {
    try {
      setLoading(true);

      // Only load podcasts if we don't have them cached or if the cache is old
      const cachedData = sessionStorage.getItem("podcasts");
      const cacheTimestamp = sessionStorage.getItem("podcastsCacheTimestamp");
      const cacheAge = cacheTimestamp
        ? Date.now() - parseInt(cacheTimestamp)
        : Infinity;
      const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

      if (!forceRefresh && cachedData && cacheAge < CACHE_DURATION) {
        setPodcasts(JSON.parse(cachedData));
        setLoading(false);
        return;
      }

      // Load all podcasts at once since we have a small number
      const { data, error } = await supabase
        .from("podcast_with_subscribers")
        .select("*")
        .order("subscriber_count", { ascending: false });

      if (error) throw error;

      if (data) {
        setPodcasts(data);
        sessionStorage.setItem("podcasts", JSON.stringify(data));
        sessionStorage.setItem("podcastsCacheTimestamp", Date.now().toString());
      }
    } catch (error) {
      console.error("Error loading podcasts:", error);
      // Clear potentially corrupted cache
      sessionStorage.removeItem("podcasts");
      sessionStorage.removeItem("podcastsCacheTimestamp");

      toast({
        title: "Error loading podcasts",
        description: "Please try refreshing the page",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const loadSubscriptions = async () => {
    try {
      const { data, error } = await supabase
        .from("user_subscriptions")
        .select("podcast_id")
        .eq("user_id", user?.id);

      if (error) throw error;
      setSubscribedPodcastIds(data.map((sub) => sub.podcast_id));
    } catch (error) {
      console.error("Error loading subscriptions:", error);
    }
  };

  const addPodcast = async (url: string) => {
    if (!user) {
      toast({
        title: "Login Required",
        description: "Please login to add podcasts",
      });
      navigate("/login");
      return;
    }
    
    // Double-check admin status directly
    const adminIds = ["8e62a312-e432-4f9e-b399-ca093abd5422"];
    const isUserAdmin = adminIds.includes(user.id);
    
    if (!isAdmin || !isUserAdmin) {
      console.log(`Blocking podcast add - Auth context isAdmin: ${isAdmin}, Direct check: ${isUserAdmin}`);
      toast({
        title: "Admin Access Required",
        description: "Only administrators can add podcasts",
        variant: "destructive"
      });
      return;
    }
    
    console.log("Admin user adding podcast");

    try {
      setLoading(true);
      const podcast = await podcastService.addPodcast(url);

      try {
        await podcastService.subscribeToPodcast(user.id, podcast.id);
      } catch (error: any) {
        if (!error.message?.includes("duplicate key")) {
          throw error;
        }
      }

      await loadPodcasts();
      await loadSubscriptions();

      toast({
        title: "Success",
        description: "Podcast added successfully",
      });
    } catch (error) {
      console.error("Error adding podcast:", error);
      toast({
        title: "Error",
        description: "Failed to add podcast. Please check the RSS feed URL.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
      setIsAddDialogOpen(false);
      setFeedUrl("");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!feedUrl) {
      toast({
        title: "Error",
        description: "Please enter a RSS feed URL",
        variant: "destructive",
      });
      return;
    }
    await addPodcast(feedUrl);
  };

  const toggleSubscription = async (podcastId: string) => {
    if (!user) {
      toast({
        title: "Login Required",
        description: "Please login to subscribe to podcasts",
      });
      navigate("/login");
      return;
    }

    try {
      const result = await podcastService.togglePodcastSubscription(
        user.id,
        podcastId,
      );

      if (result.success) {
        if (result.subscribed) {
          setSubscribedPodcastIds([...subscribedPodcastIds, podcastId]);
          toast({
            title: "Subscribed",
            description: "Podcast added to your library",
          });
        } else {
          setSubscribedPodcastIds(
            subscribedPodcastIds.filter((id) => id !== podcastId),
          );
          toast({
            title: "Unsubscribed",
            description: "Podcast removed from your library",
          });
        }
        // Refresh the podcast list to update subscriber counts
        await loadPodcasts(true);
      } else {
        throw new Error("Failed to toggle subscription");
      }
    } catch (error) {
      console.error("Error toggling subscription:", error);
      toast({
        title: "Error",
        description: "Failed to update subscription",
        variant: "destructive",
      });
    }
  };

  if (loading && podcasts.length === 0) {
    return (
      <div className="w-full h-full bg-white/5 backdrop-blur-md rounded-xl border shadow-xl border-white/25 overflow-hidden">
        <div className="p-6">
          <h2 className="text-2xl font-semibold mb-6 text-gray-800">
            Loading podcasts...
          </h2>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full bg-white/5 backdrop-blur-md rounded-xl border border-white/25 overflow-hidden flex flex-col">
      <div className="flex-none px-4 py-3 border-b border-white/10">
        <div className="flex justify-between items-center">
          <h2 className="text-base font-medium text-gray-800">Podcasts</h2>
          {/* Debug output */}
          <div className="hidden">{`User: ${!!user}, Admin: ${isAdmin}, Show button: ${!!(user && isAdmin)}`}</div>
          {user && user.id && (
            <div className="hidden">
              Direct admin check: {String([
                "8e62a312-e432-4f9e-b399-ca093abd5422"
              ].includes(user.id))}
            </div>
          )}
          {user && isAdmin && (
            <Button
              onClick={() => setIsAddDialogOpen(true)}
              disabled={loading}
              size="sm"
              className="bg-gradient-to-r from-[#735fe2] to-[#627ae2] hover:opacity-90 text-white border-transparent"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Podcast
            </Button>
          )}
        </div>
      </div>

      <div className="flex-none px-4 py-2">
        <div className="relative">
          <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-500" />
          <Input
            className="pl-9 bg-white/10 border-white/20 text-gray-800 placeholder:text-gray-500"
            placeholder="Search podcasts..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </div>

      <ScrollArea className="flex-1 px-4" ref={scrollRef}>
        <div className="space-y-2 py-2">
          {filteredPodcasts.map((podcast) => (
            <div
              key={podcast.id}
              className="flex items-start gap-4 p-3 rounded-xl border border-white/20 hover:bg-white/5 transition-colors cursor-pointer"
              onClick={() => navigate(`/podcast/${podcast.id}`)}
            >
              <img
                src={podcast.image_url}
                alt={podcast.title}
                className="w-12 h-12 rounded-lg object-cover flex-shrink-0"
                loading="lazy"
              />
              <div className="flex-1 min-w-0">
                <h3 className="font-medium text-gray-800 truncate">
                  {podcast.title}
                </h3>
                <p className="text-sm text-gray-600">
                  {podcast.subscriber_count?.toLocaleString() || 0} subscribers
                </p>
              </div>
              <Button
                variant="outline"
                size="icon"
                className="flex-shrink-0 h-8 w-8 bg-white/10 hover:bg-white/20 border-white/20"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleSubscription(podcast.id);
                }}
              >
                <Heart
                  className={`h-4 w-4 stroke-[1.5px] ${subscribedPodcastIds.includes(podcast.id) ? "fill-[#735fe2] text-[#735fe2]" : ""}`}
                />
              </Button>
            </div>
          ))}
        </div>
      </ScrollArea>

      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Podcast</DialogTitle>
            <DialogDescription>
              Enter the RSS feed URL of the podcast you want to add.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="feedUrl">RSS Feed URL</Label>
              <Input
                id="feedUrl"
                type="url"
                placeholder="https://example.com/feed.xml"
                value={feedUrl}
                onChange={(e) => setFeedUrl(e.target.value)}
                required
              />
            </div>
            <Button type="submit" disabled={loading}>
              {loading ? "Adding..." : "Add Podcast"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Search;
